/**
 * 编码版本适配器接口
 * @template T - 目标编码类型，必须继承自Record<string, unknown>
 */
export interface IEncodingVersionAdapter<
  T extends Record<string, unknown> = Record<string, unknown>
> {
  /** 适配器版本标识 */
  version: string;
  /** 判断是否匹配当前编码格式 */
  isMatch: (encoding: Record<string, unknown>) => boolean;
  /** 将编码转换为目标格式 */
  transform: (encoding: Record<string, unknown>) => T;
  /** 可选的反向转换 */
  reverse?: (encoding: T) => Record<string, unknown>;
}

/**
 * 编码兼容性管理器
 * 负责管理和应用不同版本的编码适配器
 */
export class EncodingCompatibilityManager {
  private adapters: Map<string, IEncodingVersionAdapter[]> = new Map();

  /**
   * 注册编码适配器
   * @param layerType - 图层类型
   * @param adapter - 适配器实例
   */
  registerAdapter<T extends Record<string, unknown>>(
    layerType: string,
    adapter: IEncodingVersionAdapter<T>
  ): void {
    if (!this.adapters.has(layerType)) {
      this.adapters.set(layerType, []);
    }
    this.adapters.get(layerType)!.push(adapter as IEncodingVersionAdapter);
  }

  /**
   * 应用编码适配
   * @param layerType - 图层类型
   * @param encoding - 原始编码
   * @returns 适配后的编码
   */
  adaptEncoding<T extends Record<string, unknown>>(
    layerType: string,
    encoding: Record<string, unknown>
  ): T {
    const adapters = this.adapters.get(layerType) || [];

    const matchedAdapter = adapters.find(adapter => adapter.isMatch(encoding));
    if (matchedAdapter) {
      return matchedAdapter.transform(encoding) as T;
    }

    // 如果没有匹配的适配器，返回原始编码
    return encoding as T;
  }

  /**
   * 获取指定图层类型的所有适配器
   * @param layerType - 图层类型
   * @returns 适配器数组
   */
  getAdapters(layerType: string): IEncodingVersionAdapter[] {
    return this.adapters.get(layerType) || [];
  }

  /**
   * 检查是否需要适配
   * @param layerType - 图层类型
   * @param encoding - 编码
   * @returns 是否需要适配
   */
  needsAdaptation(layerType: string, encoding: Record<string, unknown>): boolean {
    const adapters = this.adapters.get(layerType) || [];
    return adapters.some(adapter => adapter.isMatch(encoding));
  }
}

// 全局编码兼容性管理器实例
export const globalEncodingManager = new EncodingCompatibilityManager();
